import { hasPermission } from '@ctrl/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import { Percent, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  DataTable,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import {
  createDiscount,
  type DiscountRecord,
  deleteDiscount,
  listDiscounts,
} from '@/lib/retention';

/**
 * Discounts.
 *
 * Read straight from PostgREST under RLS: the table is small, bounded by
 * what a shop can be bothered to run, and every row is already visible to
 * anyone who can see the promo codes on the storefront.
 */
export const Route = createFileRoute('/discounts/')({
  component: DiscountsPage,
});

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

/**
 * What the promotion takes off.
 *
 * `discounts.value` is basis points for a percentage and cents for a fixed
 * amount -- one column, two units, both hundredths. `computePromoAmount`
 * divides by 10,000 for the former and spends the latter directly.
 */
export function discountAmount(discount: DiscountRecord): string {
  if (discount.kind === 'percentage') return `${discount.value / 100}%`;
  if (discount.kind === 'fixed_amount') return usd(discount.value);

  return discount.kind;
}

function windowLabel(discount: DiscountRecord): string {
  const format = (value: string) =>
    new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(value));

  if (discount.startsAt && discount.endsAt) {
    return `${format(discount.startsAt)} – ${format(discount.endsAt)}`;
  }

  if (discount.endsAt) return `Until ${format(discount.endsAt)}`;
  if (discount.startsAt) return `From ${format(discount.startsAt)}`;

  return 'Always';
}

const column = dataTableColumnHelper<DiscountRecord>();

/**
 * Built per render rather than at module scope, because the delete cell needs
 * the mutation that lives in the component. `useMemo` keeps the column
 * identities stable between renders, which is what the table sorts and sizes
 * by.
 */
function discountColumns(remove: (id: string) => void, canManage: boolean) {
  return column.columns([
    column.accessor('name', {
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to="/discounts/$discountId"
            params={{ discountId: row.original.id }}
            className="truncate font-medium hover:underline"
          >
            {row.original.name}
          </Link>

          {!row.original.active && <Badge variant="outline">Off</Badge>}
        </div>
      ),
    }),

    column.accessor('code', {
      header: 'Code',
      cell: ({ getValue }) => {
        const code = getValue();

        return code ? (
          <code className="text-xs">{code}</code>
        ) : (
          <span className="text-muted-foreground text-xs">Automatic</span>
        );
      },
    }),

    column.accessor((row) => discountAmount(row), {
      id: 'amount',
      header: 'Takes off',
      cell: ({ getValue }) => <span className="text-sm">{getValue()}</span>,
    }),

    column.accessor((row) => row.startsAt ?? row.endsAt ?? '', {
      id: 'window',
      header: 'When',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">{windowLabel(row.original)}</span>
      ),
    }),

    column.accessor('usedCount', {
      header: 'Used',
      meta: { className: 'text-right tabular-nums' },
      cell: ({ row }) =>
        `${row.original.usedCount}${row.original.usageLimit ? ` / ${row.original.usageLimit}` : ''}`,
    }),

    column.display({
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      meta: { className: 'text-right' },
      cell: ({ row }) =>
        canManage ? (
          <Button type="button" size="sm" variant="ghost" onClick={() => remove(row.original.id)}>
            Delete
          </Button>
        ) : null,
    }),
  ]);
}

function DiscountsPage() {
  const { activeAccountId, claims } = useAuth();
  const accountId = activeAccountId ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [globalFilter, setGlobalFilter] = useState('');

  const canManage = accountId ? hasPermission(claims, accountId, 'discounts.manage') : false;

  const discounts = useQuery({
    queryKey: ['discounts', accountId],
    queryFn: () => listDiscounts(accountId),
    enabled: Boolean(accountId),
  });

  const create = useMutation({
    mutationFn: () =>
      createDiscount(accountId, {
        name: 'New discount',
        code: null,
        kind: 'percentage',
        // Basis points: 10%.
        value: 1000,
        minSubtotalCents: 0,
        maxDiscountCents: null,
        usageLimit: null,
        usageLimitPerCustomer: null,
        stackable: false,
        stackingPriority: 100,
        allowedPaymentProviders: null,
        allowedChannels: null,
        allowedFulfillment: null,
        startsAt: null,
        endsAt: null,
        // Draft until somebody has actually set it up.
        active: false,
      }),
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['discounts', accountId] });
      navigate({ to: '/discounts/$discountId', params: { discountId: id } });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteDiscount(accountId, id),
    onSuccess: () => {
      toast.success('Discount deleted.');
      queryClient.invalidateQueries({ queryKey: ['discounts', accountId] });
    },
    onError: (error: Error) =>
      toast.error(
        error.message.includes('violates foreign key')
          ? 'Orders used this discount, so it cannot be deleted. Switch it off instead.'
          : error.message,
      ),
  });

  const rows = discounts.data ?? [];

  const columns = useMemo(
    () => discountColumns((id) => remove.mutate(id), canManage),
    [remove.mutate, canManage],
  );

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns,
    data: rows,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: { pagination: { pageIndex: 0, pageSize: 25 } },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Discounts</h1>
          <p className="text-muted-foreground text-sm">
            {discounts.isLoading
              ? 'Loading…'
              : `${rows.length} promotion${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter by name or code…"
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="w-72"
          />

          <Button
            size="sm"
            onClick={() => create.mutate()}
            disabled={!canManage || create.isPending}
          >
            <Plus className="size-4" />
            New discount
          </Button>
        </div>
      </div>

      <DataTable
        table={table}
        columnCount={columns.length}
        isLoading={discounts.isLoading}
        onRowActivate={(row) =>
          navigate({ to: '/discounts/$discountId', params: { discountId: row.id } })
        }
        emptyMessage={
          globalFilter ? (
            'No discounts match that filter.'
          ) : (
            <span className="flex flex-col items-center gap-2">
              <Percent className="size-6" />
              <span className="text-foreground font-medium">No discounts yet</span>
              <span className="max-w-sm">
                A code shoppers type at checkout, or an automatic promotion that applies itself.
              </span>
            </span>
          )
        }
      />

      <DataTablePagination table={table} total={rows.length} />
    </div>
  );
}
